import dynamic from 'next/dynamic';
import { useState, useRef, useEffect, Fragment } from 'react';
import { CountUp } from 'use-count-up';
import { Range } from 'react-range';
import { declOfNum, JSONToQuery, sleep } from '../../helpers';
import { submitClientRequest } from '../../helpers/clientRequest';
import axios from 'axios';
import { useLocationState } from '../../context/location';
import { useMortgageDispatch } from '../../context/mortgage';
import Radio from '../Radio';
import Button from '../Button';
import Input from '../Input';
import Checkbox from '../Checkbox';
import Textarea from '../Textarea';
import MortgageCalculatorItem from './MortgageCalculatorItem';
import MortgageCalculatorSkeletonItem from './MortgageCalculatorSkeletonItem';
import styles from '../../styles/modules/mortgage/MortgageCalculator.module.scss';
import FormAgreement from '../FormAgreement';

const Modal = dynamic(() => import('../Modal'));

const MortgageCalculator = () => {
  const locationState = useLocationState();
  const setMortgageState = useMortgageDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState(null);
  const [dataOptions, setDataOptions] = useState([]);
  const [optionsProgram, setOptionsProgram] = useState([]);
  const [objectType, setObjectType] = useState('newBuilding');
  const [objectPrice, setObjectPrice] = useState(1000000);
  const [initialFee, setInitialFee] = useState(500000);
  const [mortgageDuration, setMortgageDuration] = useState(30);
  const [finalSum, setFinalSum] = useState([500000, 500000]);
  const [isExpandedVariants, setIsExpandedVariants] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSubmited, setIsSubmited] = useState(false);
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientComment, setClientComment] = useState('');
  const [agreeWithPolicy, setAgreeWithPolicy] = useState(false);
  const clientNameId = useRef();
  const clientPhoneId = useRef();

  const handleFormSubmit = () => {
    clientNameId.current.setError(clientName.length <= 3);
    clientPhoneId.current.setError(clientPhone.length !== 11);

    if (clientName.length > 3 && clientPhone.length === 11 && agreeWithPolicy) {
      submitClientRequest({
        clientName,
        clientPhone: clientPhone,
        clientComment,
        pathName: '/mortgage',
        whatComponent: 'MortgageCalculator',
        location: locationState.name,
        platform: 'web'
      }).then((isSubmitted) => {
        if (isSubmitted) setIsSubmited(true);
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(process.env.NEXT_PUBLIC_API + '/loan/options');

        if (!response.data || response.data.status !== 'ok')
          throw new Error(
            'Something wrong with getting server data! [MortgageCalculator -> fetchData()]'
          );

        setDataOptions(
          response.data.data.options.map((x) => ({
            text: x.text,
            value: x.name
          }))
        );
      } catch (error) {}
    };
    fetchData();
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      try {
        setIsLoading(true);
        setIsError(false);
        await sleep(300);

        const response = await axios.get(
          process.env.NEXT_PUBLIC_API +
            `/loan${JSONToQuery({
              objectType,
              optionsProgram: optionsProgram[0],
              objectPrice,
              initialFee,
              mortgageDuration
            })}`,
          { signal }
        );

        if (!response.data || response.data.status !== 'ok') throw new Error('');

        setData(response.data.data);
        setIsLoading(false);
        setIsError(false);
      } catch (error) {
        if (error.code !== 'ERR_CANCELED') {
          setIsLoading(false);
          setIsError(true);
        }
      }
    };
    fetchData();

    return () => controller.abort();
  }, [objectType, optionsProgram, objectPrice, initialFee, mortgageDuration]);
  useEffect(() => {
    const countedValue = Number(objectPrice) - Number(initialFee);
    setFinalSum([finalSum[1], countedValue < 0 ? 0 : countedValue]);
  }, [objectPrice, initialFee]);

  return (
    <section className="block">
      <Modal
        title="Заявка на консультацию"
        button={!isSubmited && 'Отправить'}
        buttonDisabled={!agreeWithPolicy}
        action={handleFormSubmit}
        visibility={isModalVisible}
        onClose={() => setIsModalVisible(false)}
      >
        {isSubmited ? (
          <Fragment>
            <div style={{ marginBottom: 18 }}>Спасибо за заявку. Мы скоро с Вами свяжемся!</div>
            <img
              src="/images/thumbUp.png"
              alt=""
              style={{ padding: 50, width: '100%', boxSizing: 'border-box' }}
            />
          </Fragment>
        ) : (
          <Fragment>
            <div style={{ marginBottom: 18 }}>
              Оставьте свои контактные данные и мы с Вами свяжемся в самые кратчайшие сроки, поможем
              оформить ипотеку при необходимости!
            </div>
            <Input
              title="Имя"
              inputs={[
                {
                  placeholder: 'Василий Петрович',
                  value: clientName,
                  type: 'text'
                }
              ]}
              isWrappedStyle
              ref={clientNameId}
              onChange={(v) => setClientName(v[0])}
            />
            <Input
              title="Телефон"
              inputs={[
                {
                  placeholder: locationState.phone,
                  value: clientPhone,
                  mask: 'phone',
                  type: 'tel'
                }
              ]}
              isWrappedStyle
              ref={clientPhoneId}
              onChange={(v) => setClientPhone(v[0])}
            />
            <Textarea
              title="Коммментарий"
              placeholder="Например, «Помогите с подбором ипотеки, есть 300 000 рублей первоначального взноса»"
              autoHeight
              isWrappedStyle
              onChange={(v) => setClientComment(v)}
            />
            <FormAgreement onChange={setAgreeWithPolicy} />
          </Fragment>
        )}
      </Modal>
      <div className="fixed">
        <h3>Калькулятор ипотеки</h3>
      </div>
      <div className="fixed columns">
        <div className={styles.group}>
          <Radio
            title="Тип объекта"
            radios={[
              { text: 'Новостройка', value: 'newBuilding' },
              { text: 'Вторичка', value: 'resale' }
            ]}
            selected={objectType}
            onChange={(v) => setObjectType(v)}
          />
          <Checkbox
            title="Программа"
            checkboxes={dataOptions}
            selected={optionsProgram}
            single
            onChange={(v) => {
              setOptionsProgram(v);
            }}
          />
          <div className={styles.calculator}>
            <div className={styles.calculatorItem}>
              <Input
                title="Стоимость объекта"
                inputs={[
                  {
                    placeholder: '2 500 000',
                    value: objectPrice,
                    mask: 'money',
                    max: 20000000
                  }
                ]}
                suffix="₽"
                style={{ marginBottom: 0 }}
                onChange={(newValues) => {
                  setObjectPrice(newValues[0]);
                  setMortgageState({
                    type: 'SET_MORTGAGE',
                    payload: {
                      objectPrice: newValues[0]
                    }
                  });

                  if (newValues[0] < initialFee) {
                    setInitialFee(Number(newValues[0]));
                    setMortgageState({
                      type: 'SET_MORTGAGE',
                      payload: {
                        initialFee: newValues[0]
                      }
                    });
                  }

                  setIsExpandedVariants(false);
                }}
              />
              <Range
                step={50000}
                min={100000}
                max={30000000}
                values={[objectPrice]}
                onChange={(values) => {
                  setObjectPrice(values[0]);
                  setMortgageState({
                    type: 'SET_MORTGAGE',
                    payload: {
                      objectPrice: values[0]
                    }
                  });

                  if (values[0] < initialFee) {
                    setInitialFee(Number(values[0]));
                    setMortgageState({
                      type: 'SET_MORTGAGE',
                      payload: {
                        initialFee: values[0]
                      }
                    });
                  }

                  setIsExpandedVariants(false);
                }}
                renderTrack={({ props, children }) => (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      height: 0,
                      width: 'calc(100% - 32px)',
                      margin: '0 16px'
                    }}
                  >
                    {children}
                  </div>
                )}
                renderThumb={({ props }) => (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      height: 18,
                      width: 18,
                      borderRadius: '50%',
                      margin: '0 6px',
                      backgroundColor: '#2969E0',
                      outline: 'none'
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        bottom: 4,
                        left: 4
                      }}
                    />
                  </div>
                )}
              />
            </div>
            <div className={styles.calculatorItem}>
              <Input
                title="Первоначальный взнос"
                inputs={[
                  { placeholder: '500 000', value: initialFee, mask: 'money', max: objectPrice }
                ]}
                suffix="₽"
                style={{ marginBottom: 0 }}
                onChange={(newValues) => {
                  setInitialFee(Number(newValues[0]));
                  setMortgageState({
                    type: 'SET_MORTGAGE',
                    payload: {
                      initialFee: newValues[0]
                    }
                  });

                  setIsExpandedVariants(false);
                }}
              />
              <Range
                step={50000}
                min={0}
                max={objectPrice}
                values={[initialFee]}
                onChange={(values) => {
                  setInitialFee(Number(values[0]));
                  setMortgageState({
                    type: 'SET_MORTGAGE',
                    payload: {
                      initialFee: values[0]
                    }
                  });

                  setIsExpandedVariants(false);
                }}
                renderTrack={({ props, children }) => (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      height: 0,
                      width: 'calc(100% - 32px)',
                      margin: '0 16px'
                    }}
                  >
                    {children}
                  </div>
                )}
                renderThumb={({ props }) => (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      height: 18,
                      width: 18,
                      borderRadius: '50%',
                      margin: '0 6px',
                      backgroundColor: '#2969E0',
                      outline: 'none'
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        bottom: 4,
                        left: 4
                      }}
                    />
                  </div>
                )}
              />
            </div>
            <div className={styles.calculatorItem}>
              <Input
                title="Срок кредита"
                inputs={[{ placeholder: '10', value: mortgageDuration, mask: 'money', max: 30 }]}
                suffix={declOfNum(mortgageDuration, ['год', 'года', 'лет'])}
                style={{ marginBottom: 0 }}
                onChange={(newValues) => {
                  setMortgageDuration(newValues[0]);
                  setMortgageState({
                    type: 'SET_MORTGAGE',
                    payload: {
                      mortgageDuration: newValues[0]
                    }
                  });

                  setIsExpandedVariants(false);
                }}
              />
              <Range
                step={1}
                min={1}
                max={30}
                values={[mortgageDuration]}
                onChange={(values) => {
                  setMortgageDuration(values[0]);
                  setMortgageState({
                    type: 'SET_MORTGAGE',
                    payload: {
                      mortgageDuration: values[0]
                    }
                  });

                  setIsExpandedVariants(false);
                }}
                renderTrack={({ props, children }) => (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      height: 0,
                      width: 'calc(100% - 32px)',
                      margin: '0 16px'
                    }}
                  >
                    {children}
                  </div>
                )}
                renderThumb={({ props }) => (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      height: 18,
                      width: 18,
                      borderRadius: '50%',
                      margin: '0 6px',
                      backgroundColor: '#2969E0',
                      outline: 'none'
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        bottom: 4,
                        left: 4
                      }}
                    />
                  </div>
                )}
              />
            </div>
            <div className={styles.calculatorInput}>
              <div className={styles.calculatorInputLabel}>Сумма кредита</div>
              <div className={styles.calculatorInputTitle}>
                <CountUp
                  isCounting
                  start={finalSum[0]}
                  end={finalSum[1]}
                  thousandsSeparator=" "
                  duration={1}
                  key={finalSum[1]}
                />{' '}
                ₽
              </div>
            </div>
          </div>
          <div className={styles.description}>
            С нашей помощью Вы можете взять ипотеку{' '}
            <span className={styles.descriptionAccent}>без первоначального взноса</span> и{' '}
            <span className={styles.descriptionAccent}>официального трудоустройства</span> всего{' '}
            <span className={styles.descriptionAccent}>по 2 документам</span>. Мы самостоятельно
            отправим запросы во все банки и найдем самую низкую процентную ставку для Вас. С нами
            одобрено 99% заявок от наших клиентов, в среднем банки одобряют всего 70% заявок,
            поданных самостоятельно.
          </div>
        </div>
        <div className={styles.mortgages}>
          {isLoading && (
            <>
              {[...Array(2).keys()].map((value) => (
                <MortgageCalculatorSkeletonItem key={value} />
              ))}
              <div className={styles.mortgagesHelp}>Подбираем лучшие варианты...</div>
            </>
          )}
          {isError && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <h5 style={{ marginBottom: 8 }}>Предложения не найдены</h5>
              <div style={{ opacity: 0.6, textAlign: 'center', margin: '0 18px' }}>
                Попробуйте изменить параметры калькулятора или же отправьте заявку и мы бесплатно
                подберем лучший вариант для вас
              </div>
              <Button style={{ marginTop: 12 }} onClick={() => setIsModalVisible(true)}>
                Отправить заявку
              </Button>
            </div>
          )}
          {!isLoading && !isError && (
            <Fragment>
              {data.slice(0, isExpandedVariants ? data.length : 3).map((value, index) => (
                <MortgageCalculatorItem
                  title={value.bank}
                  rate={value.min_percent}
                  payment={value.payment}
                  best={value.best}
                  image={value.file}
                  key={index}
                />
              ))}
              {data.length > 3 && (
                <button
                  type="button"
                  className={styles.mortgagesLoadMore}
                  onClick={() => setIsExpandedVariants(!isExpandedVariants)}
                >
                  {isExpandedVariants ? 'Скрыть предложения' : 'Показать еще варианты...'}
                </button>
              )}
            </Fragment>
          )}
        </div>
      </div>
    </section>
  );
};

export default MortgageCalculator;
